import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  PenTool,
  BarChart3,
  FileText,
  Copy,
  Shield,
  Plus,
  Send,
  CheckCircle,
  Clock,
  AlertCircle,
  PartyPopper,
  X,
} from "lucide-react";
import { esignApi } from "../../services/api";
import EsignOverviewTab from "./EsignOverviewTab";
import EsignEnvelopesTab from "./EsignEnvelopesTab";
import EsignTemplatesTab from "./EsignTemplatesTab";
import EsignAuditLogTab from "./EsignAuditLogTab";
import CreateEnvelopeWizard from "./CreateEnvelopeWizard";

type TabKey = "overview" | "envelopes" | "templates" | "audit";

interface DashboardData {
  sentThisMonth: number;
  completionRate: number;
  avgTurnaroundHours: number;
  pendingCount: number;
}

const tabs: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "overview", label: "Overview", icon: <BarChart3 className="h-4 w-4" /> },
  { key: "envelopes", label: "All Requests", icon: <FileText className="h-4 w-4" /> },
  { key: "templates", label: "Templates", icon: <Copy className="h-4 w-4" /> },
  { key: "audit", label: "Audit Log", icon: <Shield className="h-4 w-4" /> },
];

const PRIMARY = "var(--color-primary)";
const HOVER = "var(--color-primary-hover)";

function formatTurnaround(hours: number): string {
  if (!hours || hours <= 0) return "N/A";
  if (hours < 24) return "< 1 day";
  if (hours < 48) return "1 day";
  return `${Math.round(hours / 24)} days`;
}

const EsignPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [dashboard, setDashboard] = useState<DashboardData>({
    sentThisMonth: 0,
    completionRate: 0,
    avgTurnaroundHours: 0,
    pendingCount: 0,
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoadingSummary(true);
    try {
      const res = await esignApi.dashboard();
      const data = res.data?.data || res.data;
      setDashboard({
        sentThisMonth: data?.sentThisMonth || 0,
        completionRate: data?.completionRate || 0,
        avgTurnaroundHours: data?.avgTurnaroundHours || 0,
        pendingCount: data?.pendingCount || 0,
      });
    } catch (err) {
      console.error("Failed to fetch e-sign dashboard:", err);
    } finally {
      setLoadingSummary(false);
    }
  };

  const handleCreateComplete = () => {
    setShowCreateWizard(false);
    setShowSuccessModal(true);
    setRefreshKey((k) => k + 1);
    fetchDashboard();
    // Auto-dismiss after 4 seconds
    setTimeout(() => setShowSuccessModal(false), 4000);
  };

  const summaryCards = [
    {
      label: "Sent This Month",
      value: String(dashboard.sentThisMonth),
      icon: <Send className="h-5 w-5" />,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      label: "Completion Rate",
      value: `${Math.round(dashboard.completionRate)}%`,
      icon: <CheckCircle className="h-5 w-5" />,
      color: "text-emerald-600",
      bg: "bg-emerald-50",
    },
    {
      label: "Avg Turnaround",
      value: formatTurnaround(dashboard.avgTurnaroundHours),
      icon: <Clock className="h-5 w-5" />,
      color: "text-amber-600",
      bg: "bg-amber-50",
    },
    {
      label: "Pending Signatures",
      value: String(dashboard.pendingCount),
      icon: <AlertCircle className="h-5 w-5" />,
      color: "text-purple-600",
      bg: "bg-purple-50",
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <PenTool className="h-6 w-6" style={{ color: PRIMARY }} />
            <h1 className="text-2xl font-bold text-slate-800">E-Signatures</h1>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Create, send, and track document signatures
          </p>
        </div>
        <button
          onClick={() => setShowCreateWizard(true)}
          className="flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-xl transition-colors"
          style={{ backgroundColor: PRIMARY }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = HOVER;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = PRIMARY;
          }}
        >
          <Plus className="h-4 w-4" />
          New Request
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {summaryCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-5"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">{card.label}</p>
                {loadingSummary ? (
                  <div className="h-8 w-24 bg-slate-100 rounded animate-pulse mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-slate-800 mt-1">{card.value}</p>
                )}
              </div>
              <div className={`p-3 rounded-xl ${card.bg}`}>
                <span className={card.color}>{card.icon}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-6">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="relative flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap"
              style={{
                color: activeTab === tab.key ? PRIMARY : "#64748b",
              }}
            >
              {tab.icon}
              {tab.label}
              {tab.key === "envelopes" && dashboard.pendingCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-700 leading-none">
                  {dashboard.pendingCount}
                </span>
              )}
              {activeTab === tab.key && (
                <motion.div
                  layoutId="esign-tab-indicator"
                  className="absolute bottom-0 left-0 right-0 h-0.5"
                  style={{ backgroundColor: PRIMARY }}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeTab === "overview" && (
          <EsignOverviewTab onCreateEnvelope={() => setShowCreateWizard(true)} />
        )}
        {activeTab === "envelopes" && <EsignEnvelopesTab refreshKey={refreshKey} />}
        {activeTab === "templates" && <EsignTemplatesTab />}
        {activeTab === "audit" && <EsignAuditLogTab />}
      </motion.div>

      {/* Create Envelope Wizard */}
      <CreateEnvelopeWizard
        open={showCreateWizard}
        onClose={() => setShowCreateWizard(false)}
        onCreated={handleCreateComplete}
      />

      {/* Success Toast */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
            className="fixed bottom-6 right-6 z-50 bg-white rounded-2xl shadow-2xl border border-emerald-200 p-5 max-w-sm"
          >
            <button
              onClick={() => setShowSuccessModal(false)}
              className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-emerald-50">
                <PartyPopper className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">
                  Sent for Signing!
                </h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Your signing request has been sent. Signers will receive an email with a link to sign.
                </p>
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    setActiveTab("envelopes");
                  }}
                  className="mt-3 text-xs font-semibold transition-colors"
                  style={{ color: PRIMARY }}
                >
                  View All Requests →
                </button>
              </div>
            </div>
            {/* Auto-dismiss progress bar */}
            <motion.div
              initial={{ scaleX: 1 }}
              animate={{ scaleX: 0 }}
              transition={{ duration: 4, ease: "linear" }}
              className="absolute bottom-0 left-0 right-0 h-1 bg-emerald-400 rounded-b-2xl origin-left"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EsignPage;
